import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, DollarSign, Users, Lightbulb, TrendingUp, Building2, AlertTriangle, Map, LayoutGrid, Flame, Star, Zap, Filter, Bell, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAppContext } from "@/lib/onboardingContext";
import { formatCityName } from "@/lib/utils";
import { getCityCenter } from "@/lib/neighborhoodCoordinates";
import { EXPANDED_NEIGHBORHOODS, calculateMatchScore, sortNeighborhoods, type ExpandedNeighborhood, type SortMode } from "@/lib/neighborhoodData";
import { buildSelectedNeighborhood } from "@/lib/neighborhoodRent";
import { getCurrencySymbol } from "@/lib/currency";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";

const CITY_WARNINGS: Record<string, string> = {
  "new york": "NYC requires a Certificate of Occupancy change for new food businesses — budget 60–120 days and $280–$2,000+.",
  "los angeles": "LA requires a separate grease trap permit and Cal/OSHA compliance for kitchen safety.",
  "chicago": "Chicago winters affect outdoor seating permits — seasonal patios require separate applications each year.",
  "houston": "Houston has no zoning laws — always verify space usage directly with your landlord.",
  "austin": "Austin's restaurant market is the fastest growing in the US — expect high competition and rising rents.",
  "nashville": "Music venues and restaurants with live entertainment require separate entertainment permits.",
  "miami": "Miami-Dade requires a separate Business Tax Receipt that renews annually by Sep 30.",
  "london": "UK requires a Designated Premises Supervisor (DPS) with a personal licence for alcohol.",
  "manchester": "Manchester rents are rising fast — much cheaper than London but the gap is closing.",
  "edinburgh": "Scottish licensing law is different from England. Tourist season heavily affects foot traffic.",
  "milan": "Italian bureaucracy is notoriously slow — budget 3–6 months for all permits.",
  "rome": "Rome's centro storico has strict heritage restrictions — verify renovation limits before signing.",
  "barcelona": "Barcelona has a tourist accommodation moratorium — verify your licence eligibility early.",
  "madrid": "Madrileños eat late — restaurants don't fill until 9:30pm. Adjust staffing accordingly.",
  "dubai": "Dubai requires a trade licence before you can do anything — budget AED 15,000–50,000.",
  "toronto": "Canadian food premises need a DineSafe inspection before opening — allow 4–8 weeks.",
  "vancouver": "Vancouver's liquor licensing is separate from food — apply to LCRB early.",
  "singapore": "Singapore requires SFA licence — processing takes 2–4 weeks. Don't sign a lease without it.",
  "são paulo": "Brazil's Alvará de Funcionamento can take 60–120 days — start early.",
  "sao paulo": "Brazil's Alvará de Funcionamento can take 60–120 days — start early.",
  "sydney": "NSW Food Authority licence required. Budget A$400–600 and 4–8 weeks for approval.",
  "melbourne": "Victorian liquor licence costs vary widely A$500–3,000. Apply months before your planned opening.",
  "brisbane": "Queensland food business licence via Brisbane City Council — allow 4–6 weeks.",
  "florence": "Florence centro storico has strict commercial zoning — verify food service is permitted.",
  "valencia": "Valencia is Spain's cheapest major city for F&B — rents are rising, lock in early.",
};

const BADGE_COLORS: Record<string, string> = {
  "Trendy": "bg-pink-500/10 text-pink-600 border-pink-500/20",
  "Up & Coming": "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  "Established": "bg-blue-500/10 text-blue-600 border-blue-500/20",
  "Tourist": "bg-amber-500/10 text-amber-600 border-amber-500/20",
  "Business District": "bg-violet-500/10 text-violet-600 border-violet-500/20",
  "Local Gem": "bg-teal-500/10 text-teal-600 border-teal-500/20",
};

function matchBorderColor(score: number) {
  if (score >= 85) return "border-l-emerald-500";
  if (score >= 70) return "border-l-amber-400";
  return "border-l-muted";
}

/** Map component for cities WITH neighborhood data */
function NeighborhoodMap({ cityKey, neighborhoods }: {
  cityKey: string;
  neighborhoods: (ExpandedNeighborhood & { matchScore: number })[];
}) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!mapRef.current) return;
    if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; }

    const center = getCityCenter(cityKey);
    if (!center) return;

    const map = L.map(mapRef.current, {
      center: [center.lat, center.lng],
      zoom: center.zoom,
      scrollWheelZoom: true,
    });

    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>',
      maxZoom: 19,
    }).addTo(map);

    neighborhoods.forEach((n) => {
      const markerColor = n.matchScore >= 85 ? "#22c55e" : n.matchScore >= 70 ? "#f59e0b" : "#94a3b8";
      const icon = L.divIcon({
        className: "custom-marker",
        html: `<div style="width:30px;height:30px;background:${markerColor};border:3px solid white;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;"><span style="color:white;font-size:11px;font-weight:700;">${n.matchScore}</span></div>`,
        iconSize: [30, 30], iconAnchor: [15, 15], popupAnchor: [0, -15],
      });

      L.marker(n.coords, { icon }).addTo(map).bindPopup(`
        <div style="font-family:system-ui,sans-serif;min-width:220px;">
          <div style="font-weight:700;font-size:14px;">${n.name}</div>
          <div style="font-size:11px;color:#6b7280;margin:2px 0 8px;">${n.badge} · ${n.rentDisplay}</div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;font-size:12px;">
            <div><span style="color:#6b7280;">Traffic:</span><br/><strong>${n.traffic}</strong></div>
            <div><span style="color:#6b7280;">Avg Spend:</span><br/><strong>${n.avgSpend}</strong></div>
            <div><span style="color:#6b7280;">Competition:</span><br/><strong>${n.competition}</strong></div>
            <div><span style="color:#6b7280;">Match:</span><br/><strong>${n.matchScore}%</strong></div>
          </div>
          <div style="margin-top:8px;font-size:11px;color:#6b7280;">Best for: ${n.bestFor.join(", ")}</div>
        </div>
      `, { maxWidth: 300 });
    });

    mapInstance.current = map;
    setTimeout(() => map.invalidateSize(), 100);

    return () => { if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; } };
  }, [cityKey, neighborhoods]);

  return (
    <div ref={mapRef} className="w-full rounded-xl border border-border overflow-hidden" style={{ height: 400 }} />
  );
}

/** Map component for cities WITHOUT neighborhood data — just shows the city centered */
function CityPlaceholderMap({ cityKey }: { cityKey: string }) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!mapRef.current) return;
    if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; }

    const center = getCityCenter(cityKey);
    if (!center) return;

    const map = L.map(mapRef.current, {
      center: [center.lat, center.lng],
      zoom: center.zoom,
      scrollWheelZoom: true,
    });

    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>',
      maxZoom: 19,
    }).addTo(map);

    mapInstance.current = map;
    setTimeout(() => map.invalidateSize(), 100);

    return () => { if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; } };
  }, [cityKey]);

  return (
    <div ref={mapRef} className="w-full rounded-xl border border-border overflow-hidden" style={{ height: 350 }} />
  );
}

export default function Locations() {
  const navigate = useNavigate();
  const { onboarding, selectedNeighborhood, setSelectedNeighborhood } = useAppContext();
  const cityKey = (onboarding?.city || "miami").toLowerCase().trim();
  const businessType = onboarding?.businessType || "restaurant";
  const cityName = formatCityName(cityKey);
  const warning = Object.entries(CITY_WARNINGS).find(([k]) => cityKey.includes(k) || k.includes(cityKey))?.[1];
  const cityCenter = getCityCenter(cityKey);
  const cs = getCurrencySymbol(cityKey);

  const [viewMode, setViewMode] = useState<"map" | "cards">("map");
  const [sortMode, setSortMode] = useState<SortMode>("match");

  // Get neighborhoods with match scores
  const neighborhoods = useMemo(() => {
    const cityData = EXPANDED_NEIGHBORHOODS[cityKey]
      || Object.entries(EXPANDED_NEIGHBORHOODS).find(([k]) => cityKey.includes(k) || k.includes(cityKey))?.[1]
      || [];
    const withScores = cityData.map(n => ({
      ...n,
      matchScore: calculateMatchScore(n, businessType),
    }));
    return sortNeighborhoods(withScores, sortMode);
  }, [cityKey, businessType, sortMode]);

  const hasNeighborhoodData = neighborhoods.length > 0;

  // Only treat the saved selection as "active" if it matches the current city
  const activeSelection = selectedNeighborhood?.cityKey === cityKey ? selectedNeighborhood : null;

  const handleSelect = (n: ExpandedNeighborhood) => {
    if (activeSelection?.name === n.name) {
      setSelectedNeighborhood(null);
      toast("Neighborhood deselected — using city average rent.");
      return;
    }
    const targetSqft = onboarding?.sqftMax || onboarding?.sqftMin || 1200;
    const built = buildSelectedNeighborhood(cityKey, n, targetSqft);
    setSelectedNeighborhood(built);
    toast.success(`${n.name} selected — rent applied to Budget & Forecast.`);
  };

  const businessLabel = onboarding?.businessType
    ? onboarding.businessType.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())
    : "Restaurant";

  return (
    <div className="min-h-screen bg-background pb-16">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-lg font-medium text-foreground">{cityName} Neighborhood Guide</h1>
            <p className="text-sm text-muted-foreground">
              {hasNeighborhoodData
                ? `Best areas for your ${businessLabel} — sorted by match`
                : `Neighborhood analysis for ${cityName}`}
            </p>
          </div>
        </div>
      </div>

      {/* Listings Banner */}
      <div className="bg-primary/5 border-b border-primary/10">
        <div className="container py-3 flex items-center gap-3">
          <Building2 className="w-4 h-4 text-primary shrink-0" />
          <p className="text-xs text-muted-foreground">
            📍 Live listings coming soon — use this guide to plan your location strategy in {cityName}.
          </p>
        </div>
      </div>

      <div className="container py-6 max-w-5xl">
        {/* City Warning */}
        {warning && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="mb-5 flex items-start gap-3 p-3 rounded-xl border-2 border-accent/30 bg-accent/5">
            <AlertTriangle className="w-4 h-4 text-accent shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-foreground">⚠️ {cityName} Note</p>
              <p className="text-xs text-muted-foreground">{warning}</p>
            </div>
          </motion.div>
        )}

        {/* ─── CITY HAS NEIGHBORHOOD DATA ─── */}
        {hasNeighborhoodData ? (
          <>
            {/* Controls: View toggle + Sort filters */}
            <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
              <div className="flex gap-1.5 flex-wrap">
                {([
                  { id: "match" as const, label: "Best Match", icon: Star },
                  { id: "rent" as const, label: "Lowest Rent", icon: DollarSign },
                  { id: "traffic" as const, label: "Highest Traffic", icon: Users },
                  { id: "upcoming" as const, label: "Up & Coming", icon: Zap },
                ]).map(f => (
                  <button key={f.id} onClick={() => setSortMode(f.id)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${sortMode === f.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
                    <f.icon className="w-3 h-3" /> {f.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-1 bg-muted rounded-lg p-1">
                <button onClick={() => setViewMode("map")}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === "map" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                  <Map className="w-3.5 h-3.5" /> Map
                </button>
                <button onClick={() => setViewMode("cards")}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === "cards" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                  <LayoutGrid className="w-3.5 h-3.5" /> Cards
                </button>
              </div>
            </div>

            {/* Match Score Legend */}
            <div className="flex items-center gap-4 mb-5 text-xs text-muted-foreground flex-wrap">
              <Filter className="w-3.5 h-3.5" />
              <span>Matching for: <span className="font-medium text-foreground">{businessLabel}</span></span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: "#22c55e" }} /> Great match (85%+)</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: "#f59e0b" }} /> Good match (70–84%)</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: "#94a3b8" }} /> Possible ({`<`}70%)</span>
            </div>

            {/* Map View */}
            {viewMode === "map" && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                <NeighborhoodMap cityKey={cityKey} neighborhoods={neighborhoods} />
              </motion.div>
            )}

            {/* Selection banner */}
            {activeSelection ? (
              <motion.div
                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                className="mb-5 flex items-center justify-between gap-3 p-3 rounded-xl border-2 border-primary/30 bg-primary/5"
              >
                <div className="flex items-start gap-2.5 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-foreground">
                      Rent updated based on {activeSelection.name} — adjust manually if needed.
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      ~{cs}{activeSelection.monthlyRentLocal.toLocaleString()}/mo applied to Budget Planner & Forecast.
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost" size="sm"
                  className="shrink-0 h-7 px-2 text-xs"
                  onClick={() => { setSelectedNeighborhood(null); toast("Reverted to city average rent."); }}
                >
                  <X className="w-3 h-3 mr-1" /> Clear
                </Button>
              </motion.div>
            ) : (
              <p className="mb-4 text-xs text-muted-foreground">
                Tap a neighborhood card to apply its rent to your Budget Planner & Forecast. Currently using the city average.
              </p>
            )}

            {/* Cards */}
            <div className={`grid grid-cols-1 ${viewMode === "map" ? "" : "md:grid-cols-2"} gap-4`}>
              {neighborhoods.map((n, i) => {
                const isSelected = activeSelection?.name === n.name;
                return (
                <motion.div key={n.name} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                  <Card
                    role="button"
                    tabIndex={0}
                    aria-pressed={isSelected}
                    onClick={() => handleSelect(n)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleSelect(n); } }}
                    className={`relative h-full border-l-4 ${matchBorderColor(n.matchScore)} cursor-pointer transition-all hover:shadow-lg ${isSelected ? "ring-2 ring-primary ring-offset-2 ring-offset-background shadow-lg" : ""}`}
                  >
                    {isSelected && (
                      <div className="absolute top-3 right-3 z-10 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow">
                        <Check className="w-3.5 h-3.5" />
                      </div>
                    )}
                    <CardContent className="p-5 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <MapPin className="w-4 h-4 text-primary shrink-0" />
                            <h3 className="font-medium text-foreground">{n.name}</h3>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${BADGE_COLORS[n.badge] || ""}`}>
                              {n.badge}
                            </span>
                          </div>
                        </div>
                        <div className={`shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-sm font-medium text-white ${n.matchScore >= 85 ? "bg-emerald-500" : n.matchScore >= 70 ? "bg-amber-500" : "bg-muted-foreground"}`}>
                          {n.matchScore}%
                        </div>
                      </div>

                      <p className="text-xs text-muted-foreground leading-relaxed">{n.vibe}</p>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                        <div className="bg-muted/30 rounded-lg p-2">
                          <div className="text-muted-foreground">Rent</div>
                          <div className="font-medium text-foreground">{n.rentDisplay}</div>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-2">
                          <div className="text-muted-foreground">Traffic</div>
                          <div className={`font-medium ${n.traffic === "Very High" ? "text-primary" : n.traffic === "High" ? "text-primary/80" : "text-foreground"}`}>{n.traffic}</div>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-2">
                          <div className="text-muted-foreground">Avg Spend</div>
                          <div className="font-medium text-foreground">{n.avgSpend}</div>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-2">
                          <div className="text-muted-foreground">Competition</div>
                          <div className={`font-medium ${n.competition === "Low" || n.competition === "Very Low" ? "text-emerald-600" : n.competition === "Medium" ? "text-amber-600" : "text-destructive"}`}>{n.competition}</div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-1">
                        {n.bestFor.map(concept => (
                          <span key={concept} className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                            {concept}
                          </span>
                        ))}
                      </div>

                      <div className="flex items-start gap-1.5 text-xs bg-primary/5 rounded-lg p-2.5">
                        <Lightbulb className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                        <span className="text-muted-foreground"><span className="font-medium text-foreground">Insider tip:</span> {n.insiderTip}</span>
                      </div>

                      <div className="pt-1">
                        <Button
                          type="button"
                          size="sm"
                          variant={isSelected ? "secondary" : "default"}
                          className="w-full h-8 text-xs"
                          onClick={(e) => { e.stopPropagation(); handleSelect(n); }}
                        >
                          {isSelected ? (<><Check className="w-3.5 h-3.5 mr-1" /> Selected — applied to Budget &amp; Forecast</>) : "Apply this rent to Budget & Forecast"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
                );
              })}
            </div>
          </>
        ) : (
          /* ─── CITY HAS NO NEIGHBORHOOD DATA — COMING SOON ─── */
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            {/* Show centered city map */}
            {cityCenter && (
              <CityPlaceholderMap cityKey={cityKey} />
            )}

            {/* Coming soon card */}
            <Card className="border-2 border-dashed border-primary/20">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <MapPin className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-heading font-medium text-foreground">
                    Neighborhood analysis coming soon for {cityName}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                    We're researching neighborhoods, rents, foot traffic, and competition data for {cityName}.
                    You'll get match scores tailored to your {businessLabel.toLowerCase()} concept.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                  <Button
                    variant="default"
                    className="gap-2"
                    onClick={() => {
                      toast.success(`We'll notify you when ${cityName} neighborhood data launches!`);
                    }}
                  >
                    <Bell className="w-4 h-4" />
                    Notify me when it launches
                  </Button>
                  <Button variant="outline" onClick={() => navigate("/dashboard")}>
                    Back to Dashboard
                  </Button>
                </div>

                <div className="pt-4 border-t border-border mt-4">
                  <p className="text-xs text-muted-foreground">
                    💡 In the meantime, use the <button onClick={() => navigate("/assistant")} className="text-primary hover:underline font-medium">AI Assistant</button> to ask about neighborhoods in {cityName} — it knows the city well.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
